import type { RevenueTrendPoint } from "@payrollmart-invoice/shared";
import { formatDollars } from "@payrollmart-invoice/shared";
import { useMemo } from "react";
import {
	CartesianGrid,
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { formatCompactDollars, formatMonthLabel, formatMonthLong } from "./_chart-helpers";

interface RevenueSparklineProps {
	points: RevenueTrendPoint[];
}

/** Shape Recharts sees per data point — dollars (number), not formatted strings. */
interface ChartDatum {
	month: string;
	monthLabel: string;
	revenueDollars: number;
	costDollars: number;
}

const CHART_HEIGHT = 220;

export function RevenueSparkline({ points }: RevenueSparklineProps) {
	// Convert integer-cents source data to display-dollars exactly once.
	// The integer-cents invariant applies to billing math + persistence,
	// not to display formatting — Recharts is a sink, not a calc engine.
	const data = useMemo<ChartDatum[]>(
		() =>
			points.map((p) => ({
				month: p.month,
				monthLabel: formatMonthLabel(p.month),
				revenueDollars: p.revenue / 100,
				costDollars: p.cost / 100,
			})),
		[points],
	);

	if (data.length === 0) {
		return (
			<div
				data-testid="revenue-sparkline-empty"
				className="flex h-[220px] items-center justify-center text-sm text-[#6b7280]"
			>
				No revenue data for the selected window.
			</div>
		);
	}

	return (
		<div data-testid="revenue-sparkline">
			<ResponsiveContainer width="100%" height={CHART_HEIGHT}>
				<LineChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
					<CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
					<XAxis
						dataKey="monthLabel"
						stroke="#9ca3af"
						tickLine={false}
						axisLine={{ stroke: "#e5e7eb" }}
						tick={{ fontSize: 11, fill: "#9ca3af" }}
					/>
					<YAxis
						width={56}
						stroke="#9ca3af"
						tickLine={false}
						axisLine={false}
						tick={{ fontSize: 11, fill: "#9ca3af" }}
						tickFormatter={(v: number) => formatCompactDollars(v)}
					/>
					<Tooltip
						content={<CustomTooltip />}
						cursor={{ stroke: "#9ca3af", strokeDasharray: "2 3" }}
					/>
					<Line
						type="monotone"
						dataKey="revenueDollars"
						stroke="#4f46e5"
						strokeWidth={2}
						dot={false}
						activeDot={{ r: 4 }}
						isAnimationActive={false}
					/>
					<Line
						type="monotone"
						dataKey="costDollars"
						stroke="#9ca3af"
						strokeWidth={2}
						strokeDasharray="4 4"
						dot={false}
						activeDot={{ r: 4 }}
						isAnimationActive={false}
					/>
				</LineChart>
			</ResponsiveContainer>
		</div>
	);
}

/**
 * Props the custom tooltip cares about — Recharts hands its `content` slot
 * a wide bag of props (coordinate, accessibilityLayer, activeIndex, etc.),
 * but we only read `active` + `payload`. Recharts 3's exported
 * `TooltipContentProps` is strict on every field; we accept exactly what we
 * use so test fixtures don't have to reconstruct the whole shape.
 */
interface CustomTooltipProps {
	active?: boolean;
	payload?: ReadonlyArray<{ payload?: ChartDatum }>;
}

/**
 * Custom tooltip — full month name + year on top, then Revenue + Cost rows.
 * Exported for unit testing: jsdom + Recharts hover is fragile, so we test
 * the tooltip by calling this component directly with a mock payload rather
 * than simulating the hover event.
 */
export function CustomTooltip({ active, payload }: CustomTooltipProps) {
	if (!active || !payload || payload.length === 0) return null;
	const first = payload[0];
	const datum = first?.payload;
	if (!datum) return null;

	const revenueCents = Math.round(datum.revenueDollars * 100);
	const costCents = Math.round(datum.costDollars * 100);

	return (
		<div
			data-testid="revenue-sparkline-tooltip"
			className="rounded-md border border-[#e5e7eb] bg-white px-3 py-2 text-sm shadow-sm"
		>
			<div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-[#9ca3af]">
				{formatMonthLong(datum.month)}
			</div>
			<div className="flex items-center justify-between gap-6">
				<span className="text-[#6b7280]">Revenue</span>
				<span className="font-semibold text-[#1e1e20]">{formatDollars(revenueCents)}</span>
			</div>
			<div className="flex items-center justify-between gap-6">
				<span className="text-[#6b7280]">Cost</span>
				<span className="font-semibold text-[#1e1e20]">{formatDollars(costCents)}</span>
			</div>
		</div>
	);
}
